import React from 'react'
import { Flex, Image } from '@moonpig/launchpad-components'
import { styled } from '@moonpig/launchpad-utils'
import { system as s } from '@moonpig/launchpad-system'
import { CardImageWrapper } from './StyledCardImage'
import { CardImageOrientationWrapper } from './CardImageOrientationWrapper'
import { useLocaleText } from '../../locale'
import { Orientation, Variant } from '../utils/types'
import { FavouriteButton, FavouriteButtonProps } from './FavouriteButton'
import phone from '../assets/phone.png'

const StyledImg = styled.img`
  width: 100%;
  height: 100%;
  object-fit: contain;
  display: block;
  transform: translateZ(0);
  box-shadow: 1.841px 0 3.683px 0 rgba(0, 0, 0, 0.05);
`

const StyledContainer = styled(Flex)`
  ${s({
    position: 'relative',
    justifyContent: 'center',
    overflow: 'hidden',
    width: '100%',
    borderRadius: { sm: '', md: '16px' },
  })}
`

const StyledBgContainer = styled(Flex)`
  border: 3px solid rgba(0, 0, 0, 0);
  border-bottom-width: 0;
  flex: 1;
  ${s({
    minHeight: { xs: 'auto', lg: '465px' },
    justifyContent: 'center',
    alignItems: 'center',
    bgcolor: 'colorBackground03',
  })}
`

const StyledECardWrapper = styled.div`
  position: relative;
  width: 68%;
  ${s({ mt: { xs: '15%', md: '23%', lg: '15%' } })}
`

const StyledECardBgImage = styled(Image)`
  width: 100%;
  display: block;
`

const StyledECardImage = styled(Image)`
  position: absolute;
  left: 50%;
  top: 60%;
  width: 84%;
  max-height: 64%;
  object-fit: contain;
  transform: translate(-50%, -50%);
  z-index: 1;
`

const StyledPostCardImage = styled(Image)<{
  orientation: Orientation
}>`
  ${({ orientation }: { orientation: Orientation }) =>
    s({
      width: orientation === Orientation.Landscape ? '80%' : '57%',
      my: '15%',
    })}
`

const getLocaleKey = ({
  isECard,
  isPostcard,
}: {
  isECard: boolean
  isPostcard: boolean
}) => {
  if (isECard) {
    return 'card_scene_preview.standard_size_ecard'
  }
  if (isPostcard) {
    return 'card_scene_preview.postcard'
  }
  return 'card_scene_preview.standard_size'
}

export const CardScenePreview: React.FC<{
  cardOrientation: Orientation
  selectedVariant: Variant
  coverImageUrl: string
  favourite: FavouriteButtonProps
  isECard: boolean
  isPostcard: boolean
}> = ({
  cardOrientation,
  selectedVariant,
  coverImageUrl,
  favourite,
  isECard,
  isPostcard,
}) => {
  const getLocaleText = useLocaleText()
  const isPhysicalCard = !isECard && !isPostcard

  const variantImageAltTag = getLocaleText(
    getLocaleKey({
      isECard,
      isPostcard,
    }),
  )

  return (
    <StyledContainer data-testid="product-preview">
      <StyledBgContainer>
        {favourite && <FavouriteButton {...favourite} />}
        {isECard && (
          <StyledECardWrapper>
            <StyledECardBgImage
              src={phone}
              alt={getLocaleText('card_scene_preview.image_alt_text')}
              aria-hidden
            />
            <StyledECardImage src={coverImageUrl} alt={variantImageAltTag} />
          </StyledECardWrapper>
        )}
        {isPostcard && (
          <StyledPostCardImage
            src={coverImageUrl}
            alt={variantImageAltTag}
            orientation={cardOrientation}
          />
        )}
        {isPhysicalCard && (
          <CardImageWrapper
            variant={selectedVariant}
            orientation={cardOrientation}
          >
            <CardImageOrientationWrapper orientation={cardOrientation}>
              <StyledImg src={coverImageUrl} alt={variantImageAltTag} />
            </CardImageOrientationWrapper>
          </CardImageWrapper>
        )}
      </StyledBgContainer>
    </StyledContainer>
  )
}
